import React, { memo, useEffect, useMemo, useState } from 'react';
import { Button, EchartsWithTheme, Empty, Tooltip, useTheme } from '@maxtropy/components';
import titlePng from '@/assets/images/title_icon.png';
import stationIconPng from '../../../../../../imgs/station_icon.png';
import styles from './index.module.scss';
import { Progress, Spin } from 'antd';
import { getChartOption } from './chart';
import {
  apiV2ElectricMapConfigPowerStationRankingDetailPost,
  V2ElectricMapConfigPowerStationHighConsumptionPostResponse,
  V2ElectricMapConfigPowerStationRankingDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { keepOneOrLine, keepOnePercentOrLine } from '@/utils/utils';
import { isNil } from 'lodash-es';
import { DoubleRightOutlined } from '@ant-design/icons';
import { getOrderColors } from '@/pages/WgqAllMaps/utils';

export type SubStationItem = Exclude<
  V2ElectricMapConfigPowerStationRankingDetailPostResponse['list'],
  undefined
>[number];
export type CircuitItem = Exclude<
  V2ElectricMapConfigPowerStationHighConsumptionPostResponse['list'],
  undefined
>[number];

interface HighEnergyProps {
  activeId?: number;
}

const colors = ['#16DD8E', '#FFCB47', '#2D8DFF', '#CE90D1', '#FF477B'];

const HighEnergy: React.FC<HighEnergyProps> = ({ activeId }) => {
  const theme = useTheme();
  const [substationList, setSubstationList] = useState<SubStationItem[]>([]);
  const [subStationIndex, setSubStationIndex] = useState<number>(0);
  const [subLoading, setSubLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isNil(activeId)) return;
    setSubLoading(true);
    setSubStationIndex(0);
    setSubstationList([]);
    apiV2ElectricMapConfigPowerStationRankingDetailPost({ id: activeId })
      .then(res => {
        setSubstationList(res.list ?? []);
        setSubStationIndex(0);
      })
      .finally(() => {
        setSubLoading(false);
      });
  }, [activeId]);

  const currentStation = useMemo(() => {
    return substationList[subStationIndex];
  }, [substationList, subStationIndex]);

  const powerChartOption = useMemo(() => {
    return getChartOption(currentStation?.details ?? []);
  }, [currentStation]);

  const handleClick = (index: number) => {
    setSubStationIndex(index);
  };

  return (
    <>
      <div className={styles.betweenRow}>
        <div className={styles.titleHeader}>
          <img src={titlePng} alt="" />
          <div>10kV变压器负载率排名（TOP6）</div>
        </div>
        <Button
          type="link"
          disabled={isNil(currentStation?.id) || subLoading}
          onClick={() => {
            window.open(`/power/station/configuration?stationId=${currentStation.id}`, '_blank');
          }}
        >
          接线图
          <DoubleRightOutlined className="translationStyles" />
        </Button>
      </div>
      <Spin spinning={subLoading}>
        <div className={styles.container}>
          <div className={styles.leftOrder}>
            {substationList.length > 0 ? (
              substationList.map((item, index) => {
                const { backgroundColor, borderColor, progressColor } = getOrderColors(index + 1);
                return (
                  <div
                    key={index}
                    className={`${styles.card} ${index === subStationIndex ? styles.activeCard : ''}`}
                    onClick={() => handleClick(index)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div
                      className={styles.orderNum}
                      style={{
                        backgroundColor,
                        border: `2px solid ${borderColor}`,
                      }}
                    >
                      {index + 1}
                    </div>
                    <div className={styles.content}>
                      <div className={styles.leftInfo}>
                        <div>{item?.name}</div>
                        <div>{keepOnePercentOrLine(item.rate)}%</div>
                      </div>
                      <div className={styles.splitLine}></div>
                      <div>
                        <Progress
                          percent={(item.rate ?? 0) * 100}
                          status="active"
                          style={{ width: 100 }}
                          showInfo={false}
                          trailColor="rgba(255,255,255,0.2)"
                          strokeColor={progressColor}
                        />
                        <div className={styles.power}>{keepOneOrLine(item.power)}kW</div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.empty_box}>
                <Empty style={{ marginTop: 40 }} />
              </div>
            )}
          </div>

          <div className={styles.rightBox}>
            <Spin spinning={!subLoading && loading}>
              {currentStation?.details?.length === 0 ? (
                <div className={styles.empty_box}>
                  <Empty style={{ marginTop: 40 }} />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div className={styles.table}>
                    <div className={styles.row}>
                      <div className={styles.cellStation}>
                        <img src={stationIconPng} alt="" />
                        <span className={styles.stationName}>总开名称</span>
                      </div>
                      <div className={styles.cell}>实时功率</div>
                      <div className={styles.cell}>实时负载率</div>
                      <div className={styles.cell}>今日峰值功率</div>
                      <div className={styles.cell}>今日峰值负载率</div>
                    </div>
                    {(currentStation?.details ?? []).map((item, index) => (
                      <div className={styles.row} key={item.id}>
                        <div className={styles.cellDevice}>
                          <div className={styles.circle} style={{ backgroundColor: colors[index % 5] }}></div>
                          <Tooltip title={item.name}>
                            <span>{item.name}</span>
                          </Tooltip>
                        </div>
                        <div className={styles.cell}>{keepOneOrLine(item.power)}kW</div>
                        <div className={styles.cell}>{keepOnePercentOrLine(item.load)}%</div>
                        <div className={styles.cell}>{keepOneOrLine(item.summitPower)}kW</div>
                        <div className={styles.cell}>{keepOnePercentOrLine(item.summitLoad)}%</div>
                      </div>
                    ))}
                  </div>
                  <EchartsWithTheme
                    empty={{
                      isShowEmpty: false,
                      emptyNode: <Empty style={{ marginTop: 80 }} />,
                    }}
                    option={powerChartOption}
                    style={{
                      height: 320 - (currentStation?.details ?? []).length * 30,
                      marginTop: 4,
                    }}
                    theme={theme}
                  />
                </div>
              )}
            </Spin>
          </div>
        </div>
      </Spin>
    </>
  );
};

export default memo(HighEnergy);
